import { useEffect, useRef, useState } from "react";
import type { RidePoint } from "../types/ride";
import { formatDuration } from "../lib/units";

const SPEEDS = [1, 5, 20] as const;

function findIndexForElapsed(points: RidePoint[], targetT: number): number {
  // Points are time-ordered; binary search for the first index whose t >= targetT.
  let lo = 0;
  let hi = points.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid]!.t < targetT) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

export function RideScrubber({
  points,
  cursorIndex,
  onCursorChange,
}: {
  points: RidePoint[];
  cursorIndex: number;
  onCursorChange: (index: number) => void;
}) {
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(5);
  const rafRef = useRef<number | null>(null);
  const playStartRef = useRef<{ realMs: number; rideT: number } | null>(null);

  const lastT = points[points.length - 1]?.t ?? 0;
  const currentT = points[cursorIndex]?.t ?? 0;

  useEffect(() => {
    if (!playing) {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      playStartRef.current = null;
      return;
    }

    playStartRef.current = { realMs: performance.now(), rideT: points[cursorIndex]?.t ?? 0 };

    const tick = () => {
      const start = playStartRef.current;
      if (!start) return;
      const elapsedReal = performance.now() - start.realMs;
      const targetT = start.rideT + elapsedReal * speed;
      if (targetT >= lastT) {
        onCursorChange(points.length - 1);
        setPlaying(false);
        return;
      }
      onCursorChange(findIndexForElapsed(points, targetT));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, speed]);

  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 dark:border-night-800 dark:bg-night-900">
      <button
        type="button"
        onClick={() => setPlaying((p) => !p)}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white transition hover:bg-brand-700"
        aria-label={playing ? "Pause" : "Play"}
      >
        <span aria-hidden>{playing ? "⏸" : "▶️"}</span>
      </button>

      <input
        type="range"
        min={0}
        max={Math.max(0, points.length - 1)}
        value={cursorIndex}
        onChange={(e) => onCursorChange(Number(e.target.value))}
        className="h-2 flex-1 cursor-pointer accent-brand-600"
      />

      <div className="w-24 shrink-0 text-right text-sm tabular-nums text-slate-600 dark:text-night-300">
        {formatDuration(currentT / 1000)} / {formatDuration(lastT / 1000)}
      </div>

      <div className="hidden shrink-0 gap-1 sm:flex">
        {SPEEDS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setSpeed(s)}
            className={`rounded-md px-2 py-1 text-xs font-medium transition ${
              speed === s
                ? "bg-brand-600 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-night-800 dark:text-night-300 dark:hover:bg-night-700"
            }`}
          >
            {s}×
          </button>
        ))}
      </div>
    </div>
  );
}
